// Keyboard + pointer-locked mouse. The shell polls this once per sim tick to
// build a Command; mouse deltas accumulate between render frames for the camera.
// Mouse buttons are exposed as key codes 'Mouse0' (left) / 'Mouse2' (right).

export class Input {
  private held = new Set<string>();
  private pressed = new Set<string>();
  private mouseDx = 0;
  private mouseDy = 0;
  locked = false;
  /** While a menu is open, canvas clicks must not grab the pointer. */
  suppressLock = false;

  constructor(private canvas: HTMLCanvasElement) {
    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      this.held.add(e.code);
      this.pressed.add(e.code);
      // keep Tab from cycling browser focus, and F1-F4 from triggering
      // browser help/search while playing
      if (e.code === 'Tab' || /^F[1-8]$/.test(e.code)) {
        e.preventDefault();
      }
    });
    window.addEventListener('keyup', (e) => this.held.delete(e.code));
    window.addEventListener('blur', () => this.held.clear());

    canvas.addEventListener('mousedown', (e) => {
      if (!this.locked) {
        if (!this.suppressLock) {
          canvas.requestPointerLock();
          // the click that grabs the pointer must not also swing the sword
          this.pressed.clear();
        }
        return;
      }
      this.held.add(`Mouse${e.button}`);
      this.pressed.add(`Mouse${e.button}`);
    });
    window.addEventListener('mouseup', (e) => this.held.delete(`Mouse${e.button}`));
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.canvas;
      if (!this.locked) this.held.clear();
    });
    document.addEventListener('mousemove', (e) => {
      if (!this.locked) return;
      this.mouseDx += e.movementX;
      this.mouseDy += e.movementY;
    });
  }

  isHeld(code: string): boolean {
    return this.held.has(code);
  }

  /** True once per physical key press; consumed on read. */
  wasPressed(code: string): boolean {
    const hit = this.pressed.has(code);
    this.pressed.delete(code);
    return hit;
  }

  /** Accumulated mouse motion since the last call; consumed on read. */
  takeMouseDelta(): { dx: number; dy: number } {
    const d = { dx: this.mouseDx, dy: this.mouseDy };
    this.mouseDx = 0;
    this.mouseDy = 0;
    return d;
  }

  endTick(): void {
    this.pressed.clear();
  }
}
